import { Box, Typography } from '@mui/material';
import React from 'react';

import { formatCurrencyBRL } from 'utils/currency';
import type { GrowlersCartPricing } from 'utils/growlersCartPricing';

interface GrowlerPricingBreakdownProps {
  pricing: GrowlersCartPricing;
  cashbackRedeemed?: number;
  payableTotal?: number;
  /** Valor estimado de cashback que esta compra credita. */
  cashbackEarnAmount?: number;
  cashbackEarnPercent?: number;
  rowSx?: object;
  totalRowSx?: object;
  labelSx?: object;
  valueSx?: object;
  totalLabelSx?: object;
  totalValueSx?: object;
  hintSx?: object;
}

const GrowlerPricingBreakdown: React.FC<GrowlerPricingBreakdownProps> = ({
  pricing,
  cashbackRedeemed = 0,
  payableTotal,
  cashbackEarnAmount = 0,
  cashbackEarnPercent = 0,
  rowSx,
  totalRowSx,
  labelSx,
  valueSx,
  totalLabelSx,
  totalValueSx,
  hintSx,
}) => {
  const totalToShow =
    typeof payableTotal === 'number' ? payableTotal : pricing.total;

  return (
    <>
      <Box sx={rowSx}>
        <Typography sx={labelSx}>Subtotal</Typography>
        <Typography sx={valueSx}>
          R$ {formatCurrencyBRL(pricing.subtotal)}
        </Typography>
      </Box>
      {pricing.discount > 0 && (
        <Box sx={rowSx}>
          <Typography sx={labelSx}>
            Desconto ({pricing.discountPercent}%)
          </Typography>
          <Typography sx={valueSx}>
            - R$ {formatCurrencyBRL(pricing.discount)}
          </Typography>
        </Box>
      )}
      {cashbackRedeemed > 0 && (
        <Box sx={rowSx}>
          <Typography sx={labelSx}>Cashback usado</Typography>
          <Typography sx={valueSx}>
            - R$ {formatCurrencyBRL(cashbackRedeemed)}
          </Typography>
        </Box>
      )}
      <Box sx={rowSx}>
        <Typography sx={labelSx}>Entrega</Typography>
        <Typography sx={valueSx}>
          {pricing.shippingCost === 0
            ? 'GRÁTIS'
            : `R$ ${formatCurrencyBRL(pricing.shippingCost)}`}
        </Typography>
      </Box>
      {(pricing.itemsToDiscount > 0 || pricing.amountToFreeShipping > 0) && (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {pricing.itemsToDiscount > 0 && (
            <Typography variant="caption" sx={hintSx}>
              Leve mais {pricing.itemsToDiscount}{' '}
              {pricing.itemsToDiscount === 1 ? 'item' : 'itens'} e ganhe{' '}
              {pricing.discountPercent}% de desconto.
            </Typography>
          )}
          {pricing.amountToFreeShipping > 0 && (
            <Typography variant="caption" sx={hintSx}>
              Faltam R$ {formatCurrencyBRL(pricing.amountToFreeShipping)} para
              frete grátis.
            </Typography>
          )}
        </Box>
      )}
      <Box sx={totalRowSx || rowSx}>
        <Typography sx={totalLabelSx || labelSx}>Total</Typography>
        <Typography sx={totalValueSx || valueSx}>
          R$ {formatCurrencyBRL(totalToShow)}
        </Typography>
      </Box>
      {cashbackEarnAmount > 0 && (
        <Typography
          variant="body2"
          sx={{
            ...hintSx,
            display: 'block',
            mt: 1,
            fontWeight: 600,
            color: 'primary.main',
          }}
        >
          Nesta compra você ganha R$ {formatCurrencyBRL(cashbackEarnAmount)} de
          cashback
          {cashbackEarnPercent > 0 ? ` (${cashbackEarnPercent}%)` : ''}.
        </Typography>
      )}
    </>
  );
};

export default GrowlerPricingBreakdown;
